import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { GlobalErrorBoundary } from './components/GlobalErrorBoundary';
import Layout from './components/Layout';
import { PageTransition } from './components/PageTransition';
import ScrollToTop from './components/ScrollToTop';
import { CookieBanner } from './components/privacy/CookieBanner';
import { isPrivacyBannerEnabled } from './lib/config/flags';
import TestModeBadge from './components/TestModeBadge';
import Home from './components/Home';
import Bundles from './components/Bundles';
import BundlePage from './components/BundlePage';
import DomainsExplorer from './components/DomainsExplorer';
import Contact from './components/Contact';
import FAQs from './components/FAQs';
import Login from './components/Login';
import DomainPage from './components/DomainPage';
import About from './components/About';
import Info from './components/Info';
import Services from './pages/Services';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import NotFound from './pages/NotFound';
import Health from './pages/Health';
import Refund from './pages/Refund';
import ApplyFree from './pages/ApplyFree';
import Support from './pages/Support';
import SupportContact from './pages/SupportContact';
import SupportContactSuccess from './pages/SupportContactSuccess';
import SupportFaqDetail from './pages/SupportFaqDetail';
import ApplyOffer from './pages/ApplyOffer';
import BuyBIN from './pages/BuyBIN';
import Lease from './pages/Lease';
import DomainLease from './pages/DomainLease';
import Operate from './pages/Operate';
import LegalLease from './pages/LegalLease';
import Cart from './pages/Cart';
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
import CheckoutCancel from './pages/CheckoutCancel';
import CheckoutError from './pages/CheckoutError';
import BundleBuilder from './components/BundleBuilder';
import AccountLogin from './pages/AccountLogin';
import Account from './pages/Account';
import AccountComplete from './pages/AccountComplete';
import AccountEmailConfirmed from './pages/AccountEmailConfirmed';
import AccountConsulting from './pages/AccountConsulting';
import AccountSupport from './pages/AccountSupport';
import ProtectedRoute from './components/ProtectedRoute';
import LegalPrivacy from './pages/LegalPrivacy';
import LegalTerms from './pages/LegalTerms';
import LegalEscrowRefunds from './pages/LegalEscrowRefunds';
import Maintenance from './pages/Maintenance';
import { MaintenanceGuard } from './components/MaintenanceGuard';
import Press from './pages/Press';
import Lander from './pages/Lander';
import OfferStub from './pages/OfferStub';
import ApplyStub from './pages/ApplyStub';
import Ops from './pages/Ops';
import { BuilderPageGate } from './builder/BuilderPageGate';
import { ButtonExamples } from './components/examples/ButtonExamples';

// P3 Performance: Lazy-load admin pages to reduce initial bundle by ~100KB
// Heavy deps: recharts (~45KB), papaparse (~25KB), react-markdown (~30KB)
const AdminLogin = lazy(() => import('./components/AdminLogin'));
// AdminLayout not lazy-loaded to prevent Router context race condition
// It uses Router hooks (useNavigate, useLocation) which require Router context
import AdminLayout from './components/AdminLayout';
const AdminHub = lazy(() => import('./pages/AdminHub'));
const AdminOwner = lazy(() => import('./pages/AdminOwner'));
const AdminInventory = lazy(() => import('./components/AdminInventory'));
const AdminInventoryImporter = lazy(() => import('./pages/AdminInventoryImporter'));
const AdminData = lazy(() => import('./pages/AdminData'));
const AdminPrompts = lazy(() => import('./pages/AdminPrompts'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const AdminExperiments = lazy(() => import('./pages/AdminExperiments'));
const AdminExports = lazy(() => import('./pages/AdminExports'));
const AdminSEO = lazy(() => import('./pages/AdminSEO'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminCancellations = lazy(() => import('./pages/AdminCancellations'));
const AdminSupport = lazy(() => import('./pages/AdminSupport'));
const AdminConsulting = lazy(() => import('./pages/AdminConsulting'));
const AdminDiagnostics = lazy(() => import('./pages/AdminDiagnostics'));
const AdminEnv = lazy(() => import('./pages/AdminEnv'));
const AdminAnalyticsCockpit = lazy(() => import('./pages/AdminAnalyticsCockpit'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminDataGovernance = lazy(() => import('./pages/AdminDataGovernance'));
const AdminIntegrations = lazy(() => import('./pages/AdminIntegrations'));
const AdminEmailPreview = lazy(() => import('./pages/AdminEmailPreview'));
const AdminInbox = lazy(() => import('./pages/AdminInbox'));
const AdminLaunch = lazy(() => import('./pages/AdminLaunch'));
const AdminOnboarding = lazy(() => import('./pages/AdminOnboarding'));
const AdminSelfTest = lazy(() => import('./pages/AdminSelfTest'));
const AdminEnvMap = lazy(() => import('./pages/AdminEnvMap'));
const AdminPricing = lazy(() => import('./pages/AdminPricing'));
const AdminWebhooks = lazy(() => import('./pages/AdminWebhooks'));
const AdminMetrics = lazy(() => import('./pages/AdminMetrics'));
const AdminRunbook = lazy(() => import('./pages/AdminRunbook'));

// Loading fallback for lazy-loaded routes
const AdminLoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// P20250925-119: Configure React Query with SWR-style caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale-while-revalidate pattern
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      gcTime: 30 * 60 * 1000, // 30 minutes - cache retained (formerly cacheTime)
      refetchOnWindowFocus: true, // Refetch on tab focus
      refetchOnReconnect: true, // Refetch on reconnect
      retry: 1, // Only retry once on failure
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <TooltipProvider delayDuration={200}>
          <Toaster />
          <Sonner />
          <TestModeBadge />
          <GlobalErrorBoundary>
            <AuthProvider>
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
              <ScrollToTop />
              {isPrivacyBannerEnabled() && <CookieBanner />}
              <MaintenanceGuard>
                <Layout>
                  <PageTransition>
                    <BuilderPageGate>
                      <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/info" element={<Info />} />
                      <Route path="/services" element={<Services />} />
                      <Route path="/bundles" element={<Bundles />} />
                      <Route path="/bundles/builder" element={<BundleBuilder />} />
                      <Route path="/bundles/buy" element={<Navigate to="/cart" replace />} />
                      <Route path="/bundle/:slug" element={<BundlePage />} />
                      <Route path="/domains" element={<DomainsExplorer />} />
                      <Route path="/cart" element={<Cart />} />
                      <Route 
                        path="/checkout/success" 
                        element={<Navigate to="/account/complete" replace />}
                      />
                      <Route path="/checkout/cancel" element={<CheckoutCancel />} />
                      <Route path="/checkout/error" element={<CheckoutError />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/faqs" element={<FAQs />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/terms" element={<TermsOfService />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/refund" element={<Refund />} />
                      <Route path="/legal/privacy" element={<LegalPrivacy />} />
                      <Route path="/legal/terms" element={<LegalTerms />} />
                      <Route path="/legal/escrow-and-refunds" element={<LegalEscrowRefunds />} />
                      <Route path="/domain/:name" element={<DomainPage />} />
                      <Route path="/domain/:name/lease" element={<DomainLease />} />
                      <Route path="/lease" element={<Lease />} />
                      <Route path="/operate" element={<Operate />} />
                      <Route path="/legal/lease" element={<LegalLease />} />
                      <Route path="/press" element={<Press />} />
                      <Route path="/rto" element={<Navigate to="/lease" replace />} />
                      <Route path="/renttoown" element={<Navigate to="/lease" replace />} />
                      <Route path="/rent-to-own" element={<Navigate to="/lease" replace />} />
                      <Route path="/applyrto" element={<Navigate to="/lease" replace />} />
                      <Route path="/apply/rto" element={<Navigate to="/lease" replace />} />
                      <Route path="/apply/free" element={<ApplyFree />} />
                      <Route path="/apply/offer" element={<ApplyOffer />} />
                      <Route path="/offer" element={<OfferStub />} />
                      <Route path="/apply" element={<ApplyStub />} />
                      <Route path="/lander/:slug" element={<Lander />} />
                      <Route path="/buy/bin" element={<BuyBIN />} />
                      <Route path="/health" element={<Health />} />
                      <Route path="/ops" element={<Ops />} />
                      <Route path="/env" element={<Navigate to="/manage/diagnostics" replace />} />
                      {import.meta.env.VITE_SHOW_BUTTON_EXAMPLES === 'true' && (
                        <Route path="/button-examples" element={<ButtonExamples />} />
                      )}

                      {/* Account routes */}
                      <Route path="/account/login" element={<AccountLogin />} />
                      <Route path="/account/complete" element={<AccountComplete />} />
                      <Route path="/account/magic" element={<Navigate to="/account" replace />} />
                      <Route path="/account/email-confirmed" element={<AccountEmailConfirmed />} />
                      <Route path="/account" element={<Account />} />
                      <Route 
                        path="/account/consulting" 
                        element={
                          <ProtectedRoute>
                            <AccountConsulting />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/account/support" 
                        element={
                          <ProtectedRoute>
                            <AccountSupport />
                          </ProtectedRoute>
                        } 
                      />

                      {/* Support routes */}
                      <Route path="/support" element={<Support />} />
                      <Route path="/support/contact" element={<SupportContact />} />
                      <Route path="/support/contact/success" element={<SupportContactSuccess />} />
                      <Route path="/support/faq/:slug" element={<SupportFaqDetail />} />
                      <Route path="/help" element={<Navigate to="/support" replace />} />

                      {/* Admin routes - P20250925-153: Changed from /admin to /manage */}
                      {/* P3 Performance: Wrapped in Suspense for lazy loading */}
                      <Route path="/manage/login" element={
                        <Suspense fallback={<AdminLoadingFallback />}>
                          <AdminLogin />
                        </Suspense>
                      } />
                      <Route path="/manage" element={<AdminLayout />}>
                        <Route index element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminHub />
                          </Suspense>
                        } />
                        <Route path="dashboard" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminDashboard />
                          </Suspense>
                        } />
                        <Route path="owner" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminOwner />
                          </Suspense>
                        } />
                        <Route path="data" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminData />
                          </Suspense>
                        } />
                        <Route path="data-governance" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminDataGovernance />
                          </Suspense>
                        } />
                        <Route path="inventory" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminInventory />
                          </Suspense>
                        } />
                        <Route path="inventory/import" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminInventoryImporter />
                          </Suspense>
                        } />
                        <Route path="prompts" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminPrompts />
                          </Suspense>
                        } />
                        <Route path="experiments" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminExperiments />
                          </Suspense>
                        } />
                        <Route path="integrations" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminIntegrations />
                          </Suspense>
                        } />
                        <Route path="emails/preview" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminEmailPreview />
                          </Suspense>
                        } />
                        <Route path="inbox" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminInbox />
                          </Suspense>
                        } />
                        <Route path="exports" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminExports />
                          </Suspense>
                        } />
                        <Route path="pricing" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminPricing />
                          </Suspense>
                        } />
                        <Route path="webhooks" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminWebhooks />
                          </Suspense>
                        } />
                        <Route path="metrics" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminMetrics />
                          </Suspense>
                        } />
                        <Route path="runbook" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminRunbook />
                          </Suspense>
                        } />
                        <Route path="seo" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminSEO />
                          </Suspense>
                        } />
                        <Route path="users" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminUsers />
                          </Suspense>
                        } />
                        <Route path="cancellations" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminCancellations />
                          </Suspense>
                        } />
                        <Route path="settings" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminSettings />
                          </Suspense>
                        } />
                        <Route path="diagnostics" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminDiagnostics />
                          </Suspense>
                        } />
                        <Route path="env" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminEnv />
                          </Suspense>
                        } />
                        <Route path="analytics" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminAnalyticsCockpit />
                          </Suspense>
                        } />
                        <Route path="support" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminSupport />
                          </Suspense>
                        } />
                        <Route path="consulting" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminConsulting />
                          </Suspense>
                        } />
                        <Route path="launch" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminLaunch />
                          </Suspense>
                        } />
                        <Route path="onboarding" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminOnboarding />
                          </Suspense>
                        } />
                        <Route path="self-test" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminSelfTest />
                          </Suspense>
                        } />
                        <Route path="env-map" element={
                          <Suspense fallback={<AdminLoadingFallback />}>
                            <AdminEnvMap />
                          </Suspense>
                        } />
                      </Route>

                      {/* /admin redirect for SPA (Vercel also returns 404 at network level) */}
                      <Route path="/admin/*" element={<Navigate to="/manage/login" replace />} />

                      <Route path="/maintenance" element={<Maintenance />} />

                      {/* Catch-all for unknown routes */}
                      <Route path="*" element={<NotFound />} />
                      </Routes>
                    </BuilderPageGate>
                  </PageTransition>
                </Layout>
              </MaintenanceGuard>
            </Router>
            </AuthProvider>
          </GlobalErrorBoundary>
        </TooltipProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

export default App;
